import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { v2Api } from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISavingsBucket } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IGetBucketsByAccountNumberResponse {
  status?: boolean;
  message?: string;
  data?: ISavingsBucket;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetBucketsByAccountNumberResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getBucketsByAccountNumberAsync = createAsyncThunk(
  'bucketsByAccountNumber',
  async (accountNumber: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const { data } = await v2Api.get(
        `/savings/accounts/${accountNumber}`,
        getSavingsAuthConfig(state),
      );
      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const bucketsByAccountNumberGetSlice = createSlice({
  name: 'bucketsByAccountNumber/get',
  initialState,
  reducers: {
    resetGetBucketsByAccountNumber: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBucketsByAccountNumberAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getBucketsByAccountNumberAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetBucketsByAccountNumberResponse;
      })
      .addCase(getBucketsByAccountNumberAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetGetBucketsByAccountNumber } =
  bucketsByAccountNumberGetSlice.actions;

export default bucketsByAccountNumberGetSlice.reducer;
